import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  CreatePaymentRequest,
  CreatePaymentSuccessResponse,
  CreateTestimonialRequestBody,
  EventDetail,
  EventDetailApiResponse,
  TestimonialDeleteSuccessResponse,
  TestimonialMutationSuccessResponse,
  UpdateTestimonialRequestBody,
} from '../models/event.models';
import { environment } from '../../../environments/environment';

export interface EventListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'upcoming' | 'past' | 'all';
}

export interface EventListResponse {
  events: EventDetail[];
  total: number;
  page: number;
  limit: number;
}

@Injectable({ providedIn: 'root' })
export class EventService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Busca evento por ID
   */
  getById(id: string): Observable<EventDetail> {
    return this.http.get<EventDetail>(`${this.apiUrl}/events/${id}`);
  }

  /**
   * Detalhe completo do evento (app autenticado).
   * GET /private/app/events/get_detail.json?id={public_id}
   */
  getDetail(publicId: string): Observable<EventDetailApiResponse> {
    const params = new HttpParams().set('id', publicId);
    return this.http.get<EventDetailApiResponse>(
      `${this.apiUrl}/private/app/events/get_detail.json`,
      { params },
    );
  }

  /**
   * Cria cobrança/checkout para o evento informado.
   * POST /private/app/events/create_payment.json
   */
  createPayment(eventId: string): Observable<CreatePaymentSuccessResponse> {
    const payload: CreatePaymentRequest = { event_id: eventId };
    return this.http.post<CreatePaymentSuccessResponse>(
      `${this.apiUrl}/private/app/events/create_payment.json`,
      payload,
    );
  }

  /** GET /private/app/events/testimonial.json?id={numeric_id} */
  getTestimonial(testimonialId: number): Observable<TestimonialMutationSuccessResponse> {
    const params = new HttpParams().set('id', String(testimonialId));
    return this.http.get<TestimonialMutationSuccessResponse>(
      `${this.apiUrl}/private/app/events/testimonial.json`,
      { params },
    );
  }

  /** POST /private/app/events/create_testimonial.json */
  createTestimonial(body: CreateTestimonialRequestBody): Observable<TestimonialMutationSuccessResponse> {
    return this.http.post<TestimonialMutationSuccessResponse>(
      `${this.apiUrl}/private/app/events/create_testimonial.json`,
      body,
    );
  }

  /** PUT /private/app/events/update_testimonial.json */
  updateTestimonial(body: UpdateTestimonialRequestBody): Observable<TestimonialMutationSuccessResponse> {
    return this.http.put<TestimonialMutationSuccessResponse>(
      `${this.apiUrl}/private/app/events/update_testimonial.json`,
      body,
    );
  }

  /** DELETE /private/app/events/delete_testimonial.json?id={numeric_id} */
  deleteTestimonial(testimonialId: number): Observable<TestimonialDeleteSuccessResponse> {
    const params = new HttpParams().set('id', String(testimonialId));
    return this.http.delete<TestimonialDeleteSuccessResponse>(
      `${this.apiUrl}/private/app/events/delete_testimonial.json`,
      { params },
    );
  }

  /**
   * Lista todos os eventos com paginação e filtros
   */
  list(params?: EventListParams): Observable<EventListResponse> {
    let httpParams = new HttpParams();
    
    if (params?.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params?.limit) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }
    if (params?.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params?.status) {
      httpParams = httpParams.set('status', params.status);
    }

    return this.http.get<EventListResponse>(`${this.apiUrl}/events`, { params: httpParams });
  }

  /**
   * Cria um novo evento
   */
  create(event: Partial<EventDetail>): Observable<EventDetail> {
    return this.http.post<EventDetail>(`${this.apiUrl}/events`, event);
  }

  /**
   * Atualiza um evento existente
   */
  update(id: string, event: Partial<EventDetail>): Observable<EventDetail> {
    return this.http.put<EventDetail>(`${this.apiUrl}/events/${id}`, event);
  }

  /**
   * Deleta um evento
   */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/events/${id}`);
  }

  /**
   * Inscreve um usuário em um evento
   */
  register(eventId: string, lotId: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.apiUrl}/events/${eventId}/register`,
      { lotId }
    );
  }

  /**
   * Lista participantes de um evento
   */
  getAttendees(eventId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/events/${eventId}/attendees`);
  }
}
